import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import useEnvironmentalData from "../hooks/useEnvironmentalData";
import { EXAMPLE_LOCATIONS, monitorPath } from "../lib/locations";
import { Icon } from "./SiteChrome";

const places = [
  EXAMPLE_LOCATIONS[0],
  EXAMPLE_LOCATIONS[3],
  EXAMPLE_LOCATIONS[7],
];
const value = (number, suffix = "") =>
  Number.isFinite(number) ? `${Math.round(number)}${suffix}` : "—";
const dayLabel = (date) =>
  new Date(`${date}T12:00:00Z`).toLocaleDateString("en-GB", {
    weekday: "short",
    timeZone: "UTC",
  });

export function WeatherPreview() {
  const [place, setPlace] = useState(places[0]);
  const [refresh, setRefresh] = useState(0);
  const { loading, feeds } = useEnvironmentalData(
    "climate",
    place.lat,
    place.lon,
    refresh,
  );
  const feed = feeds[0];
  const current = feed?.current;
  const forecast = (feed?.forecast || []).slice(0, 7);
  const temperatures = forecast
    .flatMap((day) => [day.tempMin, day.tempMax])
    .filter(Number.isFinite);
  const low = temperatures.length ? Math.min(...temperatures) : 0;
  const range = temperatures.length
    ? Math.max(1, Math.max(...temperatures) - low)
    : 1;
  const timestamp = feed?.dataTime ? new Date(feed.dataTime) : null;
  const validTime = timestamp && Number.isFinite(timestamp.getTime());
  const older =
    validTime && Date.now() - timestamp.getTime() > 6 * 60 * 60 * 1000;
  return (
    <div className="weather-showcase">
      <div className="weather-showcase-top">
        <span>
          <Icon name="climate" size={18} /> WEATHER / MODEL ESTIMATES
        </span>
        <div
          className="weather-city-switch"
          role="group"
          aria-label="Preview city"
        >
          {places.map((city) => (
            <button
              type="button"
              key={city.name}
              onClick={() => setPlace(city)}
              aria-pressed={place.name === city.name}
            >
              {city.name}
            </button>
          ))}
        </div>
      </div>
      <div
        className="weather-showcase-body"
        aria-live="polite"
        aria-busy={loading}
      >
        <div className="weather-now">
          <p className="weather-coordinates">
            {Math.abs(place.lat).toFixed(2)}°{place.lat >= 0 ? "N" : "S"} /{" "}
            {Math.abs(place.lon).toFixed(2)}°{place.lon >= 0 ? "E" : "W"}
          </p>
          <h3>
            {place.name}
            <span>{place.country}</span>
          </h3>
          <div className="weather-temperature">
            {loading ? "—" : value(current?.temperature)}
            <span>°C</span>
          </div>
          <p className="weather-condition">
            {loading
              ? "Fetching weather…"
              : current?.weather || "Weather unavailable"}
          </p>
          <div className="weather-submetrics">
            <span>
              Wind <b>{loading ? "—" : value(current?.windSpeed, " km/h")}</b>
            </span>
            <span>
              Humidity <b>{loading ? "—" : value(current?.humidity, "%")}</b>
            </span>
          </div>
        </div>
        <div className="weather-outlook">
          <div className="weather-outlook-heading">
            <h4>The week ahead</h4>
            <span>Daily high / low · °C</span>
          </div>
          {loading ? (
            <div className="weather-pending" role="status">
              <span className="weather-loader" />
              Loading the latest available model…
            </div>
          ) : forecast.length ? (
            <div
              className="weather-days"
              aria-label="Seven-day temperature forecast"
            >
              {forecast.map((day) => {
                const available =
                  Number.isFinite(day.tempMin) && Number.isFinite(day.tempMax);
                return (
                  <div className="weather-day" key={day.date}>
                    <time dateTime={day.date}>
                      {dayLabel(day.date)}
                      <small>
                        {new Date(`${day.date}T12:00:00Z`).getUTCDate()}
                      </small>
                    </time>
                    <div className="weather-range">
                      <span>{value(day.tempMax, "°")}</span>
                      <div className="weather-range-track" aria-hidden="true">
                        {available && (
                          <i
                            style={{
                              bottom: `${((day.tempMin - low) / range) * 65 + 7}%`,
                              height: `${Math.max(3, ((day.tempMax - day.tempMin) / range) * 65)}%`,
                            }}
                          />
                        )}
                      </div>
                      <span>{value(day.tempMin, "°")}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="weather-pending">
              <p>Weather data is temporarily unavailable for this location.</p>
              <button type="button" onClick={() => setRefresh(refresh + 1)}>
                Try again <span aria-hidden="true">↗</span>
              </button>
            </div>
          )}
          <div className="weather-provenance">
            <span>
              {validTime ? (
                <>
                  {older ? "Older model data · " : "Data time · "}
                  <time dateTime={feed.dataTime}>
                    {timestamp.toLocaleString("en-GB", {
                      day: "numeric",
                      month: "short",
                      hour: "2-digit",
                      minute: "2-digit",
                      timeZone: "UTC",
                    })}{" "}
                    UTC
                  </time>
                </>
              ) : (
                "Data time unavailable"
              )}
            </span>
            <a
              href="https://open-meteo.com/en/docs"
              target="_blank"
              rel="noopener noreferrer"
            >
              Open-Meteo · CC BY 4.0 ↗
            </a>
          </div>
        </div>
      </div>
      <div className="weather-showcase-bottom">
        <p>
          A starting point. Explore all five environmental perspectives for this
          location.
        </p>
        <Link to={monitorPath(place, "climate")}>
          Explore {place.name} <Icon name="arrow" size={20} />
        </Link>
      </div>
    </div>
  );
}

export default function WeatherShowcase() {
  const target = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (typeof IntersectionObserver !== "function") {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "180px" },
    );
    observer.observe(target.current);
    return () => observer.disconnect();
  }, []);
  return (
    <div ref={target} className="weather-mount">
      {visible ? (
        <WeatherPreview />
      ) : (
        <div className="weather-placeholder">
          Weather preview loads as you explore.
        </div>
      )}
    </div>
  );
}
